/**
 * Blob-grant browser primitives — the ONE home for the two non-obvious
 * gestures every document surface needs once it holds a short-lived,
 * single-file SAS grant.
 *
 * Why this file exists. The popup dance below was written three times
 * (DocumentsTab for the record page's filing cabinet, AttachmentsBlock
 * for card files, and now the intake drawer's client-documents section),
 * each with a comment saying it was copied rather than re-derived. The
 * knowledge it encodes is small and entirely counter-intuitive, which is
 * exactly the kind of thing that must not live in three places:
 *
 *   1. A grant arrives ASYNCHRONOUSLY, but popup blockers only allow
 *      window.open during the synchronous part of a click gesture. So the
 *      tab is opened blank ON the click and pointed at the URL later.
 *   2. Passing 'noopener' to window.open RETURNS NULL — it takes the
 *      handle away, so there is nothing left to point at the grant. The
 *      opener link therefore has to be severed by hand instead.
 *
 * These are deliberately plain functions, not a hook: each surface owns
 * its own mutation wiring (different query keys to invalidate, different
 * local state to reset), and only the browser gesture is shared. Keeping
 * the seam here means the surfaces cannot drift on the part that is
 * genuinely identical.
 */

/**
 * Open a blank tab on the click gesture, with its opener severed.
 *
 * Call this SYNCHRONOUSLY inside the click handler, then hand the
 * returned handle to `pointViewerAt` once the grant resolves. A null
 * return means the browser refused the popup.
 */
export function openBlankViewerTab(): Window | null {
  const viewer = window.open('', '_blank');
  // 'noopener' as a window.open feature would have returned null and
  // taken the handle away, so the link is severed by hand.
  if (viewer) viewer.opener = null;
  return viewer;
}

/**
 * Point an already-open viewer tab at a resolved grant URL. When the
 * synchronous open was refused, falls back to a direct open (which may
 * itself be blocked — there is nothing further to try).
 */
export function pointViewerAt(viewer: Window | null, url: string): void {
  if (viewer) {
    viewer.location.href = url;
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Save a grant to disk. The grant URL is single-file, short-lived, and
 * names the file itself via content-disposition, so a plain anchor click
 * is the whole mechanism — no fetch, no blob juggling.
 */
export function saveGrantToDisk(url: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
